// ABOUTME: Hook loading a find & replace run from GET /api/find-replace/runs/:runId.
// ABOUTME: Polls every 2s while the run is scanning or applying, so progress and results show up live.

import { useCallback, useEffect, useRef, useState } from 'react';

import { FindReplaceApiError, getFindReplaceRun } from '../api/findReplaceApi';
import { ACTIVE_RUN_STATUSES, FindReplaceRunDto } from '../types';

export const RUN_POLL_INTERVAL_MS = 2000;

/**
 * @param forcePoll keeps polling a run that is not active yet, e.g. right after an apply was
 *   requested and before the job flips the run from `ready` to `applying`.
 */
export function useFindReplaceRun(runId: string | null, forcePoll = false) {
    const [run, setRun] = useState<FindReplaceRunDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // A response of a run that is no longer the current one must not overwrite the state.
    const currentRunId = useRef(runId);
    currentRunId.current = runId;

    const refetch = useCallback(async () => {
        if (!runId) return;
        setLoading(true);
        try {
            const data = await getFindReplaceRun(runId);
            if (currentRunId.current === runId) {
                setRun(data);
                setError(null);
            }
        } catch (e) {
            if (currentRunId.current === runId) {
                setError(e instanceof Error ? e : new Error(String(e)));
            }
        } finally {
            if (currentRunId.current === runId) setLoading(false);
        }
    }, [runId]);

    useEffect(() => {
        setRun(null);
        setError(null);
        refetch();
    }, [refetch]);

    // A transient error keeps the polling going; a run that is gone (expired, deleted) stops it.
    const isGone = error instanceof FindReplaceApiError && error.status === 404;
    const isActive =
        !!run &&
        run.id === runId &&
        (ACTIVE_RUN_STATUSES.includes(run.status) || forcePoll) &&
        !isGone;

    useEffect(() => {
        if (!isActive) return undefined;
        const interval = setInterval(refetch, RUN_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isActive, refetch]);

    return { run: run && run.id === runId ? run : null, loading, error, refetch };
}
